import {
	Card,
	Divider,
	Pagination,
	Form,
	Modal,
	Button,
	Input,
	message,
} from 'antd';
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
// import moment from 'moment';
import SearchFilter from '../../component/Filter';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { getCurrencies } from '../../methods/redux/reducers/currency';
import { getSafeHavenTransferStatus } from '../../methods/redux/reducers/payment-gateway';
import VirtualFiatAccountItems from './VirtualFiatAccountItems';
import NativeNumber from '../../component/Numeral';
import remakeQueryString from '../../methods/utils/remake-query-string';
import { retrieveVirtualFiatAccounts } from '../../methods/redux/reducers/virtual-fiat-account';

export const Style = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	gap: 1em;
`;
export const MainSection = styled(Card)`
	border-radius: 10px;
	gap: 2em;
`;

export const StyledFormItem = styled(Form.Item)`
	.ant-row {
		display: block;
	}
`;

const VirtualFiatAccounts = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const [refresh, setRefresh] = useState(0);
	const [loading, setLoading] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const [
		isSafeHavenTransferStatusModalVisible,
		setIsSafeHavenTransferStatusModalVisible,
	] = useState(false);
	const { currencies } = useAppSelector((store) => store.currencies);

	const { localSwapRates } = useAppSelector((store) => store.swap);
	const { data } = useAppSelector((store) => store.virtualFiatAccount);
	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: String(localSwapRates.limit),
			});
		}

		dispatch(getCurrencies({}));
	}, []);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());

		if (Object.keys(queryData).length > 0) {
			dispatch(retrieveVirtualFiatAccounts({ ...queryData }));
		} else {
			dispatch(retrieveVirtualFiatAccounts(queryData));
		}
	}, [searchParams, refresh]);

	const currencySelect = currencies.map((item: any) => ({
		name: item.symbol,
		value: item._id,
	}));

	const handleSafeHavenTransferStatus = () => {
		setIsSafeHavenTransferStatusModalVisible(true);
	};

	const handleSafeHavenTransferStatusModalCancel = () => {
		setIsSafeHavenTransferStatusModalVisible(false);
		form.resetFields();
	};

	const submitSafeHavenTransferStatus = async (value: any) => {
		setLoading(true);
		try {
			await dispatch(getSafeHavenTransferStatus(value.sessionId));
			setIsSafeHavenTransferStatusModalVisible(false);
			navigate(
				`/core-modules/virtual-fiat-accounts/safe-haven/transfer-status/${value.sessionId}`
			);
		} catch (error: any) {
			message.error(
				error?.response?.data?.message ||
					'Failed to fetch transfer status details'
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Card>
			<h1>Virtual Fiat Accounts </h1>
			<div style={{ background: '#f8f8ff', padding: '2px 6px' }}>
				<p>Manage Virtual Fiat Accounts linked to user accounts.</p>
			</div>
			<div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
				<Button
					type="primary"
					onClick={handleSafeHavenTransferStatus}
					style={{ marginBottom: '5px' }}
				>
					Validate Safe Haven Transfer Status
				</Button>
			</div>
			<br />
			<Divider />

			<SearchFilter
				title="Search Virtual Fiat Accounts"
				searchFilters={[
					{
						label: 'User Id',
						name: 'userId',
						htmlElement: 'input',
					},
					{
						label: 'Account Number',
						name: 'accountNumber',
						htmlElement: 'input',
					},
					{
						label: 'Status',
						name: 'status',
						htmlElement: 'select',
						selectOptions: [
							{ name: 'Active', value: 'active' },
							{ name: 'Pending', value: 'pending' },
							{ name: 'Deleted', value: 'deleted' },
							{ name: 'Initiated', value: 'initiated' },
							{ name: 'Blocked', value: 'blocked' },
							{ name: 'Closed', value: 'closed' },
						],
					},
					{
						label: 'Provider Key',
						name: 'providerKey',
						htmlElement: 'select',
						selectOptions: [
							{ name: 'SAFE HAVEN', value: 'SAFE_HAVEN' },
							{ name: 'BELL BANK', value: 'BELLBANK' },
							{ name: 'PALM PAY', value: 'PALMPAY' },
						],
					},
					{
						label: 'IS Short Lived',
						name: 'isShortLived',
						htmlElement: 'select',
						selectOptions: [
							{ name: 'true', value: 'true' },
							{ name: 'false', value: 'false' },
						],
					},
					{
						label: 'Page Id',
						name: 'pageId',
						htmlElement: 'input',
					},
					{
						label: 'Per Page',
						name: 'perPage',
						htmlElement: 'input',
					},
				]}
				retrieveQueryString={updateQueryString}
			/>

			<br />

			<p>
				Total of{' '}
				<strong>
					<NativeNumber value={data?.paginator?.itemCount} /> items
				</strong>{' '}
				found
			</p>

			<br />

			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={data.paginator.itemCount}
				current={data.paginator.currentPage}
				pageSize={data.paginator.perPage}
			/>

			<br />

			{data.itemsList.map((item: any) => (
				<VirtualFiatAccountItems
					key={item?._id}
					data={item}
					setRefresh={setRefresh}
					currencySelect={currencySelect}
				/>
			))}

			<Divider />

			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={data.paginator.itemCount}
				current={data.paginator.currentPage}
				pageSize={data.paginator.perPage}
				showSizeChanger
				showQuickJumper
			/>
			<Modal
				title="Safe Haven Transfer Status"
				open={isSafeHavenTransferStatusModalVisible}
				onCancel={handleSafeHavenTransferStatusModalCancel}
				footer={null}
			>
				<Form form={form} onFinish={submitSafeHavenTransferStatus}>
					<StyledFormItem
						name="sessionId"
						label="Session ID"
						rules={[{ required: true, message: 'Please input session ID' }]}
					>
						<Input placeholder="Enter session ID" />
					</StyledFormItem>
					<Form.Item>
						<Button type="primary" htmlType="submit" loading={loading}>
							{loading ? 'Fetching...' : 'Get Status'}
						</Button>
						<Button
							onClick={handleSafeHavenTransferStatusModalCancel}
							style={{ marginLeft: '8px' }}
						>
							Cancel
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</Card>
	);
};

export default VirtualFiatAccounts;
